import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin } from "lucide-react";
import { Section } from "@/components/ui/section";
import { Container } from "@/components/ui/container";
import { BrandButton } from "@/components/ui/brand-button";
import GtaMap from "@/components/GtaMap";
import Seo from "@/components/Seo";
import { SERVICE_CITIES } from "@/data/service-area";
import { COMPANY } from "@/lib/constants";
import { TRACKS } from "@/lib/tracks";
import { PAGE_META } from "@/lib/seo-constants";
import { localBusinessSchema, breadcrumbListSchema } from "@/lib/jsonld";

const AREA = {
    residential: { audience: "homeowners", metaKey: "serviceArea" },
    commercial: { audience: "commercial properties", metaKey: "commercialServiceArea" },
    industrial: { audience: "industrial sites", metaKey: "industrialServiceArea" },
};

export default function ServiceArea({ trackKey = "residential" }) {
    const track = TRACKS[trackKey];
    const cfg = AREA[trackKey] || AREA.residential;
    return (
        <>
            <Seo
                {...PAGE_META[cfg.metaKey]}
                path={track.serviceArea}
                jsonLd={[
                    localBusinessSchema(),
                    breadcrumbListSchema([
                        ...(trackKey === "residential"
                            ? []
                            : [{ name: track.label, path: track.home }]),
                        { name: "Service Area", path: track.serviceArea },
                    ]),
                ]}
            />
            {/* Hero */}
            <Section dark className="py-20 md:py-24" data-testid="servicearea-hero">
                <Container>
                    <div className="max-w-3xl">
                        <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold mb-4">
                            Service Area
                        </p>
                        <h1
                            className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-wide leading-none text-white"
                            data-testid="servicearea-title"
                        >
                            Serving the GTA &amp;
                            <br />
                            <span className="text-brand-coral">Golden Horseshoe.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-base md:text-lg text-white/80 font-light italic leading-relaxed">
                            Based in North York — building across the region.
                        </p>
                    </div>
                </Container>
            </Section>

            {/* Map + cities */}
            <Section className="py-12 md:py-16" data-testid="servicearea-map-cities">
                <Container size="wide">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
                        {/* Map */}
                        <div className="lg:col-span-7" data-testid="servicearea-map">
                            <div className="border border-brand-dark/10 overflow-hidden">
                                <GtaMap />
                            </div>
                            <p className="mt-3 text-xs text-brand-gray italic">
                                Stylized service-area map — not to scale.
                            </p>
                        </div>

                        {/* City list */}
                        <div className="lg:col-span-5" data-testid="servicearea-cities">
                            <p className="text-xs uppercase tracking-[0.25em] text-brand-coral font-semibold mb-3">
                                Cities We Serve
                            </p>
                            <h2 className="font-display text-3xl sm:text-4xl tracking-wide text-brand-dark leading-none mb-6">
                                Click your city to start.
                            </h2>
                            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-2">
                                {SERVICE_CITIES.map((c) => (
                                    <li key={c.name}>
                                        <Link
                                            to={`${track.contact}?city=${encodeURIComponent(c.name)}`}
                                            className="group flex items-center justify-between gap-2 bg-brand-dark text-white px-3 py-2.5 hover:bg-brand-coral transition-colors duration-200"
                                            data-testid={`servicearea-city-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                        >
                                            <span className="flex items-center gap-2 min-w-0">
                                                <MapPin
                                                    className="h-3.5 w-3.5 shrink-0 text-brand-cyan group-hover:text-white transition-colors"
                                                    strokeWidth={2.5}
                                                />
                                                <span className="font-semibold text-xs uppercase tracking-wider truncate">
                                                    {c.name}
                                                </span>
                                            </span>
                                            {c.hq ? (
                                                <span className="text-[9px] font-bold uppercase tracking-wider bg-brand-coral text-white px-1.5 py-0.5 group-hover:bg-white group-hover:text-brand-coral transition-colors">
                                                    HQ
                                                </span>
                                            ) : (
                                                <ArrowRight
                                                    className="h-3.5 w-3.5 shrink-0 text-white/40 group-hover:text-white transition-all group-hover:translate-x-0.5"
                                                    strokeWidth={2.25}
                                                />
                                            )}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-4 text-xs text-brand-dark/60">
                                {SERVICE_CITIES.length} cities and growing — clicking a
                                city pre-fills your quote request.
                            </p>
                        </div>
                    </div>
                </Container>
            </Section>

            {/* Don't see your city */}
            <Section dark className="py-14 md:py-20" data-testid="servicearea-coverage">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                        <div className="lg:col-span-7">
                            <p className="text-xs uppercase tracking-[0.25em] text-brand-cyan font-semibold mb-3">
                                Don't see your city?
                            </p>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-wide text-white leading-tight">
                                We serve more than the list — call to confirm.
                            </h2>
                            <p className="mt-4 text-base text-white/70 max-w-xl">
                                Reformity serves {cfg.audience} across the GTA &amp; Golden
                                Horseshoe. Call{" "}
                                <a
                                    href={COMPANY.phoneHref}
                                    className="text-brand-cyan font-semibold hover:underline"
                                >
                                    {COMPANY.phone}
                                </a>{" "}
                                to confirm coverage for your address.
                            </p>
                        </div>
                        <div className="lg:col-span-5 flex flex-col gap-3 lg:items-end">
                            <BrandButton asChild variant="primary" size="lg" className="w-full lg:w-auto">
                                <Link to={track.contact} data-testid="servicearea-cta-quote">
                                    Get a Free Quote
                                    <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
                                </Link>
                            </BrandButton>
                            <BrandButton asChild variant="outline" tone="onDark" size="lg" className="w-full lg:w-auto">
                                <a href={COMPANY.phoneHref}>
                                    <Phone className="h-4 w-4" strokeWidth={2.25} />
                                    Call {COMPANY.phone}
                                </a>
                            </BrandButton>
                        </div>
                    </div>
                </Container>
            </Section>
        </>
    );
}
